import type { JSX } from 'react';

/**
 * N5.8 / R8 (round-6) — Agent lifecycle FSM badge.
 *
 * Renders one of the 10 canonical states from
 * `phase-neg1/interaction-design.json::agent_lifecycle_fsm`. Driven
 * by the `/ws/agent-activity` WebSocket channel — the badge's state
 * prop updates whenever a new `AgentHealthChanged` event arrives for
 * the agent.
 */
export type AgentLifecycleState =
  | 'idle'
  | 'queued'
  | 'loading_context'
  | 'executing'
  | 'self_correcting'
  | 'awaiting_approval'
  | 'complete'
  | 'failed'
  | 'escalated'
  | 'terminated';

export interface AgentLifecycleBadgeProps {
  state: AgentLifecycleState;
  title?: string;
}

const STATE_COLOUR: Record<AgentLifecycleState, string> = {
  idle: 'bg-status-idle text-fg-primary',
  queued: 'bg-status-info text-bg-base',
  loading_context: 'bg-status-info/80 text-bg-base',
  executing: 'bg-status-active text-bg-base',
  self_correcting: 'bg-status-warning text-bg-base',
  awaiting_approval: 'bg-status-warning text-bg-base',
  complete: 'bg-status-success text-bg-base',
  failed: 'bg-status-error text-bg-base',
  escalated: 'bg-status-warning/80 text-bg-base',
  terminated: 'bg-status-idle/60 text-fg-muted',
};

const STATE_LABEL: Record<AgentLifecycleState, string> = {
  idle: 'Idle',
  queued: 'Queued',
  loading_context: 'Loading',
  executing: 'Running',
  self_correcting: 'Correcting',
  awaiting_approval: 'Awaiting approval',
  complete: 'Complete',
  failed: 'Failed',
  escalated: 'Escalated',
  terminated: 'Terminated',
};

export function AgentLifecycleBadge({
  state,
  title,
}: AgentLifecycleBadgeProps): JSX.Element {
  const colour = STATE_COLOUR[state];
  const label = STATE_LABEL[state];
  return (
    <span
      role="status"
      aria-live="polite"
      title={title ?? label}
      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-2xs font-semibold uppercase tracking-wider ${colour}`}
    >
      <span aria-hidden="true" className="h-1.5 w-1.5 rounded-full bg-current" />
      {label}
    </span>
  );
}
